import { Fragment, useState } from "react";
import { toast } from "sonner";
import { useFetch } from "../../lib/useFetch";
import { api, download, errMsg, eur, fileUrl, fmtDate, todayIso } from "../../lib/api";
import { FileDown, StickyNote, Trash2 } from "lucide-react";
import { PayPalAlerts } from "./AdminPayPalAlerts";
import { Input } from "../../components/ui/input";
import { DateInput } from "../../components/site/DateInput";
import { AdminH } from "./AdminLayout";

const PayForm = ({ o, onDone }) => {
  const [f, setF] = useState({ amount: o.balance, date: todayIso(), method: "Bank transfer" });
  const pay = async () => {
    try { await api.post(`/admin/orders/${o.id}/payments`, { ...f, amount: Number(f.amount) }); toast.success("Payment recorded on the enrolment"); onDone(); } catch (e) { toast.error(errMsg(e)); onDone(); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`order-pay-form-${o.id}`}>
      <Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="h-9 w-28" data-testid={`order-pay-amount-${o.id}`} />
      <DateInput value={f.date} onChange={(v) => setF({ ...f, date: v })} className="w-40" inputClassName="h-9" data-testid={`order-pay-date-${o.id}`} />
      <select value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} className="h-9 rounded-lg border border-slate-200 px-2 text-sm">{["Bank transfer", "Cash", "Card (POS)"].map((m) => <option key={m}>{m}</option>)}</select>
      <button onClick={pay} className="btn-buoy !py-1.5" data-testid={`order-pay-submit-${o.id}`}>Record</button>
    </div>
  );
};

const OrderReceipts = ({ onChange }) => {
  const { data, reload } = useFetch("/admin/order-receipts?status=pending");
  const [amounts, setAmounts] = useState({});
  const act = async (r, approve) => {
    if (!approve && !window.confirm("Reject this receipt?")) return;
    try {
      await api.post(`/admin/order-receipts/${r.id}/verify`, { approve, amount: Number(amounts[r.id] ?? Math.min(r.amount, r.order_balance)) });
      toast.success(approve ? "Receipt verified: payment split over the enrolment" : "Receipt rejected");
    } catch (e) {
      toast.error(errMsg(e));
    }
    reload();
    onChange();
  };
  if (!data?.length) return null;
  return (
    <div className="mb-8 space-y-2" data-testid="order-receipts">
      <p className="font-display text-lg font-bold text-[#0A2540]">Enrolment receipts to verify</p>
      {data.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`order-receipt-${r.id}`}>
          <p className="text-sm"><b>{r.order_number}</b> · {r.user.first_name} {r.user.last_name} · {eur(r.amount)} (balance {eur(r.order_balance)}) · {fmtDate(r.created_at)} ·{" "}
            <a href={fileUrl(r.path)} target="_blank" rel="noreferrer" className="underline">{r.filename || "receipt"}</a></p>
          <div className="flex items-center gap-2">
            <input type="number" value={amounts[r.id] ?? Math.min(r.amount, r.order_balance)} onChange={(e) => setAmounts({ ...amounts, [r.id]: e.target.value })} className="h-9 w-28 rounded-xl border border-slate-300 px-2 text-sm" title="Amount to record (€)" data-testid={`order-receipt-verify-amount-${r.id}`} />
            <button onClick={() => act(r, true)} className="btn-buoy !py-1.5" data-testid={`order-receipt-approve-${r.id}`}>Verify & record</button>
            <button onClick={() => act(r, false)} className="rounded-full border border-rose-300 px-4 py-1.5 text-sm font-semibold text-rose-600" data-testid={`order-receipt-reject-${r.id}`}>Reject</button>
          </div>
        </div>
      ))}
    </div>
  );
};

const OrderDetails = ({ o, onDone }) => {
  const [note, setNote] = useState(o.admin_note || "");
  const [codes, setCodes] = useState(Object.fromEntries(o.charters.map((c) => [c.registration_id, c.charter_code || ""])));
  const save = async () => {
    try {
      await api.patch(`/admin/orders/${o.id}`, { admin_note: note, charter_codes: codes });
      toast.success("Enrolment updated");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3 bg-slate-50 p-4" data-testid={`order-details-${o.id}`}>
      <label className="block text-xs font-semibold text-slate-600">Internal notes (admin only, never shown to families or staff)
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1 w-full rounded-xl border border-slate-300 p-2 text-sm" data-testid={`order-note-${o.id}`} /></label>
      {o.charters.map((c) => (
        <label key={c.registration_id} className="flex flex-wrap items-center gap-3 text-sm">
          <span className="min-w-[260px]">Charter <b>{c.charter}</b> · {c.sailor} · {c.event_title}</span>
          <input value={codes[c.registration_id]} onChange={(e) => setCodes({ ...codes, [c.registration_id]: e.target.value })} placeholder="Charter number / code" className="h-9 w-48 rounded-xl border border-slate-300 px-2" data-testid={`order-charter-code-${c.registration_id}`} />
        </label>
      ))}
      <button onClick={save} className="btn-buoy !py-1.5" data-testid={`order-details-save-${o.id}`}>Save</button>
    </div>
  );
};

export default function AdminOrders() {
  const { data, reload } = useFetch("/admin/orders");
  const [open, setOpen] = useState(null);
  const [details, setDetails] = useState(null);
  const del = async (o) => {
    if (!window.confirm(`Delete enrolment ${o.number}? Its registrations, payments and receipts are deleted too and the places are freed. A used discount code stays used. This can't be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${o.id}`);
      toast.success(`Enrolment ${o.number} deleted`);
      reload();
    } catch (e) {
      toast.error(errMsg(e), { duration: 10000 });
    }
  };
  return (
    <div data-testid="admin-orders">
      <AdminH title="Enrolments" sub="Each cart checkout is one enrolment with a single payment position. A payment recorded here is split automatically over the enrolment's registrations (clinic, charter, services)." />
      <PayPalAlerts onChange={reload} />
      <OrderReceipts onChange={reload} />
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>{["Enrolment", "Customer", "Date", "Items", "Discount", "Total", "Paid", "Balance", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data || []).map((o) => (
              <Fragment key={o.id}>
              <tr className="align-top" data-testid={`order-row-${o.id}`}>
                <td className="px-4 py-3 font-mono font-semibold">{o.number}<button onClick={() => download(`/admin/orders/${o.id}/pdf`, `${o.number}.pdf`)} className="ml-2 align-middle text-slate-400 hover:text-[#0A2540]" title="Download PDF" data-testid={`order-pdf-${o.id}`}><FileDown size={15} /></button>
                  <button onClick={() => setDetails(details === o.id ? null : o.id)} className="ml-1 align-middle text-slate-400 hover:text-[#0A2540]" title="Notes & charter code" data-testid={`order-details-toggle-${o.id}`}><StickyNote size={15} /></button>
                  {o.admin_note && <span className="block max-w-[180px] truncate font-sans text-xs font-normal text-slate-500" data-testid={`order-note-preview-${o.id}`}>{o.admin_note}</span>}
                  {o.charters.filter((c) => c.charter_code).map((c) => <span key={c.registration_id} className="block font-sans text-xs font-normal text-[#00A8B5]" data-testid={`order-charter-code-preview-${c.registration_id}`}>Charter #{c.charter_code}</span>)}</td>
                <td className="px-4 py-3">{o.user.first_name} {o.user.last_name}<span className="block text-xs text-slate-500">{o.user.email}</span>{o.invoice && <span className={`mt-1 block text-xs ${o.invoice.required ? "font-semibold text-[#00A8B5]" : "text-slate-400"}`} data-testid={`order-invoice-${o.id}`}>{o.invoice.required ? `Invoice: ${o.invoice.mode === "other" ? "other party" : "parent"} – ${[o.invoice.data.name, o.invoice.data.vat_number && `VAT ${o.invoice.data.vat_number}`, o.invoice.data.id_card && `ID ${o.invoice.data.id_card}`, o.invoice.data.address, o.invoice.data.city, o.invoice.data.zip, o.invoice.data.country, o.invoice.data.email, o.invoice.data.phone].filter(Boolean).join(", ")}${o.invoice.data.notes ? ` · Notes: ${o.invoice.data.notes}` : ""}` : "No invoice"}</span>}</td>
                <td className="px-4 py-3">{fmtDate(o.created_at)}</td><td className="px-4 py-3">{o.registration_ids.length}</td>
                <td className="px-4 py-3">{o.discount_code ? <span className="font-mono">{o.discount_code} (−{eur(o.totals.discount)})</span> : "-"}</td>
                <td className="whitespace-nowrap px-4 py-3">{eur(o.total)}</td><td className="whitespace-nowrap px-4 py-3">{eur(o.paid)}</td><td className="whitespace-nowrap px-4 py-3 font-semibold" data-testid={`order-balance-${o.id}`}>{eur(o.balance)}</td>
                <td className="px-4 py-3 text-right">{o.balance > 0 && (open === o.id ? <PayForm o={o} onDone={() => { setOpen(null); reload(); }} />
                  : <button onClick={() => setOpen(o.id)} className="text-sm font-semibold text-[#FF5722] underline" data-testid={`order-pay-${o.id}`}>Record payment</button>)}
                  <button onClick={() => del(o)} className="ml-3 align-middle text-slate-400 hover:text-rose-500" title="Delete enrolment" data-testid={`order-delete-${o.id}`}><Trash2 size={16} /></button></td>
              </tr>
              {details === o.id && <tr><td colSpan={9} className="p-0"><OrderDetails o={o} onDone={() => { setDetails(null); reload(); }} /></td></tr>}
              </Fragment>
            ))}
            {data && !data.length && <tr><td colSpan={9} className="px-4 py-6 text-slate-500">No enrolments yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
